import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext.js';
import './PrivateLessons.css';

function MyPrivateLessonAvailability() {
  const token = sessionStorage.getItem('jwtToken');
  const { userInfo } = useAuth();

  const role = userInfo?.role || '';
  const isAdmin = ['super_admin', 'locatie_admin'].includes(role);
  const isTeacher = role === 'docent';

  const getTodayDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getEndOfCurrentYearDate = () => {
    const year = new Date().getFullYear();
    return `${year}-12-31`;
  };

  const getDefaultAvailability = () => ({
    weekday: 1,
    start_time: '',
    end_time: '',
    valid_from: getTodayDate(),
    valid_until: getEndOfCurrentYearDate(),
    is_active: true
  });

  const getDefaultException = () => ({
    exception_date: getTodayDate(),
    start_time: '',
    end_time: '',
    all_day_unavailable: true,
    reason: ''
  });

  const [teachers, setTeachers] = useState([]);
  const [selectedDocentId, setSelectedDocentId] = useState('');
  const [availability, setAvailability] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [isLoadingTeachers, setIsLoadingTeachers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newAvailability, setNewAvailability] = useState(getDefaultAvailability);
  const [newException, setNewException] = useState(getDefaultException);

  const selectedTeacher = useMemo(() => {
    return teachers.find((teacher) => teacher.docent_id === selectedDocentId) || null;
  }, [teachers, selectedDocentId]);

  const normalizeArray = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.Data)) return payload.Data;
    return [];
  };

  const getTeacherName = (teacher) => {
    if (!teacher) return '';
    return (
      `${teacher.firstname || ''} ${teacher.lastname || ''}`.trim() ||
      teacher.docent_name ||
      teacher.name ||
      teacher.email ||
      teacher.docent_id
    );
  };

  const fetchTeachers = async () => {
    if (!isAdmin) return;

    try {
      setIsLoadingTeachers(true);

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/docent/alldocent`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const teacherData = normalizeArray(res.data);
      setTeachers(teacherData);

      setSelectedDocentId((prev) => {
        if (prev) return prev;
        return teacherData.length === 1 ? teacherData[0].docent_id : '';
      });
    } catch (err) {
      console.error('Error fetching teachers', err);
      setTeachers([]);
    } finally {
      setIsLoadingTeachers(false);
    }
  };

  const fetchAvailability = async () => {
    if (isAdmin && !selectedDocentId) {
      setAvailability([]);
      setExceptions([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/private-lesson-availability/my`,
        {
          params: isAdmin ? { docent_id: selectedDocentId } : {},
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setAvailability(
        Array.isArray(res.data?.data?.availability) ? res.data.data.availability : []
      );
      setExceptions(
        Array.isArray(res.data?.data?.exceptions) ? res.data.data.exceptions : []
      );
    } catch (err) {
      console.error('Error fetching availability', err);
      setAvailability([]);
      setExceptions([]);
      alert(err?.response?.data?.error || 'Beschikbaarheid ophalen mislukt.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTeachers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, role]);

  useEffect(() => {
    if (isTeacher || (isAdmin && selectedDocentId)) {
      fetchAvailability();
    } else {
      setAvailability([]);
      setExceptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, role, selectedDocentId]);

  const combineDateAndTimeToIso = (dateValue, timeValue) => {
    if (!dateValue || !timeValue) return null;
    return new Date(`${dateValue}T${timeValue}:00.000Z`).toISOString();
  };

  const getDayStartIso = (dateValue) => new Date(`${dateValue}T00:00:00.000Z`).toISOString();
  const getDayEndIso = (dateValue) => new Date(`${dateValue}T23:59:59.999Z`).toISOString();

  const validateSelectedDocent = () => {
    if (isAdmin && !selectedDocentId) {
      alert('Selecteer eerst een docent.');
      return false;
    }

    return true;
  };

  const addAvailability = async (e) => {
    e.preventDefault();

    if (!validateSelectedDocent()) return;

    if (!newAvailability.start_time || !newAvailability.end_time) {
      alert('Starttijd en eindtijd zijn verplicht.');
      return;
    }

    if (newAvailability.end_time <= newAvailability.start_time) {
      alert('Eindtijd moet later zijn dan starttijd.');
      return;
    }

    if (
      newAvailability.valid_from &&
      newAvailability.valid_until &&
      new Date(newAvailability.valid_until) < new Date(newAvailability.valid_from)
    ) {
      alert('Geldig tot moet gelijk of later zijn dan geldig vanaf.');
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        weekday: Number(newAvailability.weekday),
        start_time: newAvailability.start_time,
        end_time: newAvailability.end_time,
        valid_from: newAvailability.valid_from || null,
        valid_until: newAvailability.valid_until || null,
        is_active: newAvailability.is_active,
        ...(isAdmin ? { docent_id: selectedDocentId } : {})
      };

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/private-lesson-availability/add`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setNewAvailability(getDefaultAvailability());
      fetchAvailability();
    } catch (err) {
      console.error('Error adding availability', err);
      alert(err?.response?.data?.error || 'Toevoegen mislukt.');
    } finally {
      setIsSaving(false);
    }
  };

  const addException = async (e) => {
    e.preventDefault();

    if (!validateSelectedDocent()) return;

    if (!newException.exception_date) {
      alert('Datum is verplicht.');
      return;
    }

    if (
      !newException.all_day_unavailable &&
      (!newException.start_time || !newException.end_time)
    ) {
      alert('Starttijd en eindtijd zijn verplicht als het geen hele dag betreft.');
      return;
    }

    if (
      !newException.all_day_unavailable &&
      newException.end_time <= newException.start_time
    ) {
      alert('Eindtijd moet later zijn dan starttijd.');
      return;
    }

    try {
      setIsSaving(true);

      const startAt = newException.all_day_unavailable
        ? getDayStartIso(newException.exception_date)
        : combineDateAndTimeToIso(newException.exception_date, newException.start_time);

      const endAt = newException.all_day_unavailable
        ? getDayEndIso(newException.exception_date)
        : combineDateAndTimeToIso(newException.exception_date, newException.end_time);

      const payload = {
        start_at: startAt,
        end_at: endAt,
        is_available: false,
        reason: newException.reason?.trim() || null,
        ...(isAdmin ? { docent_id: selectedDocentId } : {})
      };

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/private-lesson-availability/exception/add`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setNewException(getDefaultException());
      fetchAvailability();
    } catch (err) {
      console.error('Error adding exception', err);
      alert(err?.response?.data?.error || 'Toevoegen uitzondering mislukt.');
    } finally {
      setIsSaving(false);
    }
  };

  const removeAvailability = async (id) => {
    const confirmed = window.confirm('Weet je zeker dat je deze beschikbaarheid wilt verwijderen?');
    if (!confirmed) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/private-lesson-availability/delete/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      fetchAvailability();
    } catch (err) {
      console.error('Error deleting availability', err);
      alert(err?.response?.data?.error || 'Verwijderen mislukt.');
    }
  };

  const removeException = async (id) => {
    const confirmed = window.confirm('Weet je zeker dat je deze uitzondering wilt verwijderen?');
    if (!confirmed) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/private-lesson-availability/exception/delete/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      fetchAvailability();
    } catch (err) {
      console.error('Error deleting exception', err);
      alert(err?.response?.data?.error || 'Verwijderen uitzondering mislukt.');
    }
  };

  const weekdayLabel = (weekday) => {
    const map = {
      1: 'Maandag',
      2: 'Dinsdag',
      3: 'Woensdag',
      4: 'Donderdag',
      5: 'Vrijdag',
      6: 'Zaterdag',
      7: 'Zondag'
    };

    return map[weekday] || weekday;
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('nl-NL');
  };

  const formatTime = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (value) => {
    if (!value) return '-';
    return String(value).slice(0, 10);
  };

  const pageTitle = isAdmin
    ? 'Beschikbaarheid privelessen beheren'
    : 'Mijn beschikbaarheid privelessen';

  const canEditAvailability = isTeacher || (isAdmin && selectedDocentId);

  return (
    <div className="bg-white p-4">
      <h2 className="text-center">{pageTitle}</h2>
      <hr className="seperator" />

      {isAdmin && (
        <div className="mb-4">
          <label>Docent *</label>
          <select
            className="form-select rounded-0"
            value={selectedDocentId}
            onChange={(e) => {
              setSelectedDocentId(e.target.value);
              setAvailability([]);
              setExceptions([]);
            }}
            disabled={isLoadingTeachers}
          >
            <option value="">
              {isLoadingTeachers ? 'Docenten laden...' : 'Selecteer docent'}
            </option>
            {teachers.map((teacher) => (
              <option key={teacher.docent_id} value={teacher.docent_id}>
                {getTeacherName(teacher)}
              </option>
            ))}
          </select>

          {selectedTeacher && (
            <div className="alert alert-info mt-3 mb-0">
              Je beheert nu de beschikbaarheid van{' '}
              <strong>{getTeacherName(selectedTeacher)}</strong>.
            </div>
          )}
        </div>
      )}

      {isAdmin && !selectedDocentId ? (
        <div className="alert alert-light border">
          Selecteer eerst een docent om beschikbaarheid en uitzonderingen te beheren.
        </div>
      ) : (
        <>
          <h4 className="mb-3">Nieuw beschikbaarheidsblok</h4>
          <form onSubmit={addAvailability} className="mb-4">
            <div className="row">
              <div className="col-md-2 mb-2">
                <label>Weekdag</label>
                <select
                  className="form-select rounded-0"
                  value={newAvailability.weekday}
                  onChange={(e) =>
                    setNewAvailability((prev) => ({
                      ...prev,
                      weekday: Number(e.target.value)
                    }))
                  }
                  disabled={!canEditAvailability || isSaving}
                >
                  <option value={1}>Maandag</option>
                  <option value={2}>Dinsdag</option>
                  <option value={3}>Woensdag</option>
                  <option value={4}>Donderdag</option>
                  <option value={5}>Vrijdag</option>
                  <option value={6}>Zaterdag</option>
                  <option value={7}>Zondag</option>
                </select>
              </div>

              <div className="col-md-2 mb-2">
                <label>Starttijd</label>
                <input
                  type="time"
                  className="form-control rounded-0"
                  value={newAvailability.start_time}
                  onChange={(e) =>
                    setNewAvailability((prev) => ({
                      ...prev,
                      start_time: e.target.value
                    }))
                  }
                  disabled={!canEditAvailability || isSaving}
                  required
                />
              </div>

              <div className="col-md-2 mb-2">
                <label>Eindtijd</label>
                <input
                  type="time"
                  className="form-control rounded-0"
                  value={newAvailability.end_time}
                  onChange={(e) =>
                    setNewAvailability((prev) => ({
                      ...prev,
                      end_time: e.target.value
                    }))
                  }
                  disabled={!canEditAvailability || isSaving}
                  required
                />
              </div>

              <div className="col-md-2 mb-2">
                <label>Geldig vanaf</label>
                <input
                  type="date"
                  className="form-control rounded-0"
                  value={newAvailability.valid_from}
                  onChange={(e) =>
                    setNewAvailability((prev) => ({
                      ...prev,
                      valid_from: e.target.value
                    }))
                  }
                  disabled={!canEditAvailability || isSaving}
                />
              </div>

              <div className="col-md-2 mb-2">
                <label>Geldig tot</label>
                <input
                  type="date"
                  className="form-control rounded-0"
                  value={newAvailability.valid_until}
                  onChange={(e) =>
                    setNewAvailability((prev) => ({
                      ...prev,
                      valid_until: e.target.value
                    }))
                  }
                  disabled={!canEditAvailability || isSaving}
                />
              </div>

              <div className="col-md-2 mb-2 d-flex align-items-end">
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={!canEditAvailability || isSaving}
                >
                  {isSaving ? 'Opslaan...' : 'Toevoegen'}
                </button>
              </div>
            </div>
          </form>

          <h4 className="mb-3">Beschikbaarheid</h4>
          {isLoading ? (
            <p>Loading...</p>
          ) : availability.length === 0 ? (
            <p>Geen beschikbaarheid gevonden.</p>
          ) : (
            <div className="table-responsive mb-4">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Weekdag</th>
                    <th>Tijd</th>
                    <th>Van</th>
                    <th>Tot</th>
                    <th>Actie</th>
                  </tr>
                </thead>
                <tbody>
                  {availability.map((item) => (
                    <tr key={item.availability_id}>
                      <td>{weekdayLabel(item.weekday)}</td>
                      <td>{String(item.start_time).slice(0, 5)} - {String(item.end_time).slice(0, 5)}</td>
                      <td>{formatDateOnly(item.valid_from)}</td>
                      <td>{formatDateOnly(item.valid_until)}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeAvailability(item.availability_id)}
                          disabled={!canEditAvailability}
                        >
                          Verwijderen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h4 className="mb-3">Nieuwe uitzondering</h4>
          <form onSubmit={addException} className="mb-4">
            <div className="alert alert-light border">
              Gebruik dit om een docent tijdelijk niet beschikbaar te maken, bijvoorbeeld bij ziekte,
              vakantie of een afspraak.
            </div>

            <div className="row">
              <div className="col-md-3 mb-2">
                <label>Datum</label>
                <input
                  type="date"
                  className="form-control rounded-0"
                  value={newException.exception_date}
                  onChange={(e) =>
                    setNewException((prev) => ({
                      ...prev,
                      exception_date: e.target.value
                    }))
                  }
                  disabled={!canEditAvailability || isSaving}
                  required
                />
              </div>

              <div className="col-md-3 mb-2 d-flex align-items-end">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={newException.all_day_unavailable}
                    onChange={(e) =>
                      setNewException((prev) => ({
                        ...prev,
                        all_day_unavailable: e.target.checked,
                        start_time: e.target.checked ? '' : prev.start_time,
                        end_time: e.target.checked ? '' : prev.end_time
                      }))
                    }
                    disabled={!canEditAvailability || isSaving}
                    id="allDayUnavailable"
                  />
                  <label className="form-check-label" htmlFor="allDayUnavailable">
                    Hele dag niet beschikbaar
                  </label>
                </div>
              </div>

              <div className="col-md-2 mb-2">
                <label>Starttijd</label>
                <input
                  type="time"
                  className="form-control rounded-0"
                  value={newException.start_time}
                  onChange={(e) =>
                    setNewException((prev) => ({
                      ...prev,
                      start_time: e.target.value
                    }))
                  }
                  disabled={newException.all_day_unavailable || !canEditAvailability || isSaving}
                  required={!newException.all_day_unavailable}
                />
              </div>

              <div className="col-md-2 mb-2">
                <label>Eindtijd</label>
                <input
                  type="time"
                  className="form-control rounded-0"
                  value={newException.end_time}
                  onChange={(e) =>
                    setNewException((prev) => ({
                      ...prev,
                      end_time: e.target.value
                    }))
                  }
                  disabled={newException.all_day_unavailable || !canEditAvailability || isSaving}
                  required={!newException.all_day_unavailable}
                />
              </div>

              <div className="col-md-2 mb-2 d-flex align-items-end">
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={!canEditAvailability || isSaving}
                >
                  {isSaving ? 'Opslaan...' : 'Toevoegen'}
                </button>
              </div>
            </div>

            <div className="mt-2">
              <label>Reden / toelichting</label>
              <input
                type="text"
                className="form-control rounded-0"
                value={newException.reason}
                onChange={(e) =>
                  setNewException((prev) => ({
                    ...prev,
                    reason: e.target.value
                  }))
                }
                disabled={!canEditAvailability || isSaving}
                placeholder="Bijvoorbeeld vakantie, ziek, studiedag"
              />
            </div>
          </form>

          <h4 className="mb-3">Uitzonderingen</h4>
          {exceptions.length === 0 ? (
            <p>Geen uitzonderingen gevonden.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Tijd</th>
                    <th>Type</th>
                    <th>Reden</th>
                    <th>Actie</th>
                  </tr>
                </thead>
                <tbody>
                  {exceptions.map((item) => (
                    <tr key={item.exception_id}>
                      <td>{formatDate(item.start_at)}</td>
                      <td>{`${formatTime(item.start_at)} - ${formatTime(item.end_at)}`}</td>
                      <td>{item.is_available ? 'Extra beschikbaar' : 'Niet beschikbaar'}</td>
                      <td>{item.reason || '-'}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeException(item.exception_id)}
                          disabled={!canEditAvailability}
                        >
                          Verwijderen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default MyPrivateLessonAvailability;
